import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { CO7_DEFAULT_LIST_LIMIT } from './co7.constants'
import {
    addCollectionMember,
    createCollection,
    deleteCollection,
    fetchCollectionById,
    fetchCollectionMemberCandidates,
    fetchCollectionMembers,
    fetchCollections,
    removeCollectionMember,
    updateCollection,
    updateCollectionMemberRole,
} from './co7.service'

const resolveApiMessage = (message, fallback) => {
    if (message == null || message === '') return fallback
    if (typeof message === 'string') return message
    if (typeof message === 'object') {
        if (typeof message.error === 'string') return message.error
        if (typeof message.message === 'string') return message.message
        const nested = Object.values(message).find(
            (value) => typeof value === 'string' && value.trim()
        )
        if (nested) return nested
    }
    return fallback
}

const getApiError = (error, fallback) => {
    const data = error?.response?.data ?? {}
    return (
        resolveApiMessage(data.message, null) ||
        resolveApiMessage(data.error, null) ||
        resolveApiMessage(error?.message, null) ||
        fallback
    )
}

export const normalizeCo7Member = (member = {}) => {
    const user = member.user || {}
    return {
        userId: member.userId || user._id || member._id,
        name: user.name || member.name || member.userName || 'Member',
        email: user.email || member.email || '',
        role: member.role,
        profilePicture:
            user.profile ||
            user.profilePicture ||
            member.profilePicture ||
            member.profile ||
            null,
    }
}

const normalizeCollection = (collection = {}) => ({
    ...collection,
    members: Array.isArray(collection.members)
        ? collection.members.map(normalizeCo7Member)
        : [],
})

const initialState = {
    status: 'idle',
    mutationStatus: 'idle',
    error: '',
    collections: [],
    skip: 0,
    limit: CO7_DEFAULT_LIST_LIMIT,
    search: '',
    statusFilter: null,
    isNext: false,
    totalCount: 0,
    inviteUsers: [],
    inviteUsersStatus: 'idle',
    inviteUsersPage: 1,
    inviteUsersSearch: '',
    inviteUsersIsNext: false,
    activeMembers: [],
    membersStatus: 'idle',
    membersError: '',
    collectionDetail: null,
    collectionDetailStatus: 'idle',
}

export const loadCo7Collections = createAsyncThunk(
    'co7/loadCollections',
    async (
        { reset = true, search, statusFilter } = {},
        { getState, rejectWithValue }
    ) => {
        const { co7Slice } = getState()
        const nextSearch = search !== undefined ? search : co7Slice.search
        const nextStatusFilter =
            statusFilter !== undefined ? statusFilter : co7Slice.statusFilter
        const skip = reset ? 0 : co7Slice.skip
        const isDeleted =
            nextStatusFilter != null
                ? nextStatusFilter === 'archived'
                : undefined

        try {
            const response = await fetchCollections({
                skip,
                limit: co7Slice.limit ?? CO7_DEFAULT_LIST_LIMIT,
                search: nextSearch?.trim() || undefined,
                ...(typeof isDeleted === 'boolean' ? { isDeleted } : {}),
                sort: 'updatedAt',
                order: 'desc',
            })
            const body = response?.data ?? {}
            if (!body.success) {
                return rejectWithValue(
                    resolveApiMessage(body.message, 'Failed to load collections')
                )
            }
            return {
                reset,
                search: nextSearch,
                statusFilter: nextStatusFilter,
                list: Array.isArray(body.result) ? body.result : [],
                isNext: Boolean(body.isNext),
                totalCount: Number(body.totalCount) || 0,
            }
        } catch (error) {
            return rejectWithValue(getApiError(error, 'Failed to load collections'))
        }
    }
)

export const fetchCo7CollectionById = createAsyncThunk(
    'co7/fetchCollectionById',
    async (id, { rejectWithValue }) => {
        try {
            const response = await fetchCollectionById(id)
            const body = response?.data ?? {}
            if (!body.success) {
                return rejectWithValue(
                    resolveApiMessage(body.message, 'Failed to load collection')
                )
            }
            return body.result ?? null
        } catch (error) {
            return rejectWithValue(getApiError(error, 'Failed to load collection'))
        }
    }
)

export const createCo7Collection = createAsyncThunk(
    'co7/createCollection',
    async ({ name, description }, { rejectWithValue }) => {
        try {
            const response = await createCollection({ name, description })
            const body = response?.data ?? {}
            if (!body.success) {
                return rejectWithValue(
                    resolveApiMessage(body.message, 'Failed to create collection')
                )
            }
            return body.result ?? null
        } catch (error) {
            return rejectWithValue(getApiError(error, 'Failed to create collection'))
        }
    }
)

export const updateCo7Collection = createAsyncThunk(
    'co7/updateCollection',
    async ({ id, name, description, isDeleted }, { rejectWithValue }) => {
        try {
            const response = await updateCollection({
                id,
                name,
                description,
                isDeleted,
            })
            const body = response?.data ?? {}
            if (!body.success) {
                return rejectWithValue(
                    resolveApiMessage(body.message, 'Failed to update collection')
                )
            }
            return { id, name, description, isDeleted, result: body.result }
        } catch (error) {
            return rejectWithValue(getApiError(error, 'Failed to update collection'))
        }
    }
)

export const deleteCo7Collection = createAsyncThunk(
    'co7/deleteCollection',
    async (id, { rejectWithValue }) => {
        try {
            const response = await deleteCollection(id)
            const body = response?.data ?? {}
            if (!body.success) {
                return rejectWithValue(
                    resolveApiMessage(body.message, 'Failed to delete collection')
                )
            }
            return id
        } catch (error) {
            return rejectWithValue(getApiError(error, 'Failed to delete collection'))
        }
    }
)

export const loadCo7CollectionMembers = createAsyncThunk(
    'co7/loadCollectionMembers',
    async (collectionId, { rejectWithValue }) => {
        try {
            const response = await fetchCollectionMembers(collectionId, {
                skip: 0,
                limit: 100,
            })
            const body = response?.data ?? {}
            if (!body.success) {
                return rejectWithValue(
                    resolveApiMessage(body.message, 'Failed to load members')
                )
            }
            const list = Array.isArray(body.result) ? body.result : []
            return {
                collectionId,
                members: list.map(normalizeCo7Member),
            }
        } catch (error) {
            return rejectWithValue(getApiError(error, 'Failed to load members'))
        }
    }
)

export const loadCo7InviteUsers = createAsyncThunk(
    'co7/loadInviteUsers',
    async (
        { collectionId, skip = 0, search = '' } = {},
        { getState, rejectWithValue }
    ) => {
        if (!collectionId) {
            return rejectWithValue('Collection is required')
        }
        try {
            const { co7Slice } = getState()
            const nextSearch =
                search !== undefined ? search : co7Slice.inviteUsersSearch
            const response = await fetchCollectionMemberCandidates(collectionId, {
                skip,
                limit: co7Slice.limit ?? CO7_DEFAULT_LIST_LIMIT,
                search: nextSearch?.trim() || undefined,
            })
            const body = response?.data ?? {}
            if (!body.success) {
                return rejectWithValue(
                    resolveApiMessage(body.message, 'Failed to load users')
                )
            }
            return {
                collectionId,
                skip,
                search: nextSearch,
                list: Array.isArray(body.result) ? body.result : [],
                isNext: Boolean(body.isNext),
            }
        } catch (error) {
            return rejectWithValue(getApiError(error, 'Failed to load users'))
        }
    }
)

export const inviteCo7Member = createAsyncThunk(
    'co7/inviteMember',
    async ({ collectionId, userIds, role }, { rejectWithValue }) => {
        const ids = Array.isArray(userIds)
            ? userIds.filter(Boolean)
            : userIds
              ? [userIds]
              : []
        if (!ids.length) {
            return rejectWithValue('Select at least one user')
        }
        try {
            const response = await addCollectionMember({
                collectionId,
                userIds: ids,
                role,
            })
            const body = response?.data ?? {}
            if (!body.success) {
                return rejectWithValue(
                    resolveApiMessage(body.message, 'Failed to invite member')
                )
            }
            return {
                collectionId,
                userIds: ids,
                role: body.result?.role || role,
            }
        } catch (error) {
            return rejectWithValue(getApiError(error, 'Failed to invite member'))
        }
    }
)

export const changeCo7MemberRole = createAsyncThunk(
    'co7/changeMemberRole',
    async ({ collectionId, userId, role }, { rejectWithValue }) => {
        try {
            const response = await updateCollectionMemberRole({
                collectionId,
                userId,
                role,
            })
            const body = response?.data ?? {}
            if (!body.success) {
                return rejectWithValue(
                    resolveApiMessage(body.message, 'Failed to update role')
                )
            }
            return { collectionId, userId, role: body.result?.role || role }
        } catch (error) {
            return rejectWithValue(getApiError(error, 'Failed to update role'))
        }
    }
)

export const removeCo7Member = createAsyncThunk(
    'co7/removeMember',
    async ({ collectionId, userId }, { rejectWithValue }) => {
        try {
            const response = await removeCollectionMember({
                collectionId,
                userId,
            })
            const body = response?.data ?? {}
            if (!body.success) {
                return rejectWithValue(
                    resolveApiMessage(body.message, 'Failed to remove member')
                )
            }
            return { collectionId, userId }
        } catch (error) {
            return rejectWithValue(getApiError(error, 'Failed to remove member'))
        }
    }
)

const co7Slice = createSlice({
    name: 'co7Slice',
    initialState,
    reducers: {
        clearCo7InviteUsers: (state) => {
            state.inviteUsers = []
            state.inviteUsersStatus = 'idle'
            state.inviteUsersPage = 1
            state.inviteUsersSearch = ''
            state.inviteUsersIsNext = false
        },
        clearCo7MembersModal: (state) => {
            state.activeMembers = []
            state.membersStatus = 'idle'
            state.membersError = ''
            state.inviteUsers = []
            state.inviteUsersStatus = 'idle'
            state.inviteUsersPage = 1
            state.inviteUsersSearch = ''
            state.inviteUsersIsNext = false
        },
        resetCo7State: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadCo7Collections.pending, (state, action) => {
                state.status =
                    action.meta?.arg?.reset === false ? 'loadingMore' : 'loading'
                state.error = ''
            })
            .addCase(loadCo7Collections.fulfilled, (state, action) => {
                state.status = 'loaded'
                const { reset, search, statusFilter, list, isNext, totalCount } =
                    action.payload ?? {}
                state.search = search ?? state.search
                state.statusFilter = statusFilter ?? null
                const normalizedList = (list || []).map(normalizeCollection)
                state.collections = reset
                    ? normalizedList
                    : [...state.collections, ...normalizedList]
                state.skip = state.collections.length
                state.isNext = isNext
                state.totalCount = totalCount
            })
            .addCase(loadCo7Collections.rejected, (state, action) => {
                state.status = 'error'
                state.error = action.payload || 'Failed to load collections'
            })
            .addCase(fetchCo7CollectionById.pending, (state) => {
                state.collectionDetailStatus = 'loading'
            })
            .addCase(fetchCo7CollectionById.fulfilled, (state, action) => {
                state.collectionDetailStatus = 'loaded'
                state.collectionDetail = action.payload
            })
            .addCase(fetchCo7CollectionById.rejected, (state) => {
                state.collectionDetailStatus = 'error'
            })
            .addCase(createCo7Collection.pending, (state) => {
                state.mutationStatus = 'loading'
            })
            .addCase(createCo7Collection.fulfilled, (state) => {
                state.mutationStatus = 'loaded'
            })
            .addCase(createCo7Collection.rejected, (state) => {
                state.mutationStatus = 'error'
            })
            .addCase(updateCo7Collection.pending, (state) => {
                state.mutationStatus = 'loading'
            })
            .addCase(updateCo7Collection.fulfilled, (state, action) => {
                state.mutationStatus = 'loaded'
                const { id, name, description, isDeleted } =
                    action.payload ?? {}
                if (id) {
                    state.collections = state.collections.map((col) =>
                        col._id === id
                            ? {
                                  ...col,
                                  ...(name !== undefined ? { name } : {}),
                                  ...(description !== undefined
                                      ? { description }
                                      : {}),
                                  ...(typeof isDeleted === 'boolean'
                                      ? { isDeleted }
                                      : {}),
                              }
                            : col
                    )
                }
            })
            .addCase(updateCo7Collection.rejected, (state) => {
                state.mutationStatus = 'error'
            })
            .addCase(deleteCo7Collection.fulfilled, (state, action) => {
                state.collections = state.collections.filter(
                    (col) => col._id !== action.payload
                )
                state.totalCount = Math.max(0, state.totalCount - 1)
            })
            .addCase(loadCo7CollectionMembers.pending, (state) => {
                state.membersStatus = 'loading'
                state.membersError = ''
            })
            .addCase(loadCo7CollectionMembers.fulfilled, (state, action) => {
                state.membersStatus = 'loaded'
                state.activeMembers = action.payload?.members ?? []
            })
            .addCase(loadCo7CollectionMembers.rejected, (state, action) => {
                state.membersStatus = 'error'
                state.membersError = action.payload || 'Failed to load members'
                state.activeMembers = []
            })
            .addCase(loadCo7InviteUsers.pending, (state) => {
                state.inviteUsersStatus = 'loading'
            })
            .addCase(loadCo7InviteUsers.fulfilled, (state, action) => {
                state.inviteUsersStatus = 'loaded'
                const { skip = 0, search = '', list = [], isNext } =
                    action.payload ?? {}
                if (skip === 0) {
                    state.inviteUsers = list
                    state.inviteUsersPage = 1
                    state.inviteUsersSearch = search
                } else {
                    state.inviteUsers = [...state.inviteUsers, ...list]
                    state.inviteUsersPage = state.inviteUsersPage + 1
                }
                state.inviteUsersIsNext = isNext
            })
            .addCase(loadCo7InviteUsers.rejected, (state) => {
                state.inviteUsersStatus = 'error'
                state.inviteUsers = []
                state.inviteUsersPage = 1
                state.inviteUsersIsNext = false
            })
    },
})

export const { clearCo7InviteUsers, clearCo7MembersModal, resetCo7State } =
    co7Slice.actions
export const co7State = (state) => state.co7Slice || initialState
export default co7Slice.reducer
